import * as yup from 'yup';
import {
  BaseMealSchema,
  CheckboxSchema,
  CheckboxWithOptionalTextSchema,
  ICheckbox,
  ICheckboxWithText,
  IMeal,
  IScaleRating,
  OptionalScaleRatingSchema,
} from '../../../common';
import { TubeApplicationType, WeightControlDecisionType } from '../../../enums';

export const CarePlan3Schema = yup.object({
  prepareBiteSize: OptionalScaleRatingSchema.notRequired().nullable().default({}),
  eat: OptionalScaleRatingSchema.notRequired().nullable().default({}),
  drink: OptionalScaleRatingSchema.notRequired().nullable().default({}),
  malnutritionRisk: CheckboxSchema.notRequired().nullable().default({}),
  malnutrition: CheckboxSchema.notRequired().nullable().default({}),
  exsiccosisRisk: CheckboxSchema.notRequired().nullable().default({}),
  aspirationRisk: yup
    .object({
      selected: yup.boolean().default(false),
      value: yup.mixed().when('selected', {
        is: true,
        then: yup.array(yup.string().notRequired()).nullable().default([]),
      }),
    })
    .notRequired()
    .nullable()
    .default({}),
  preferencesEat: yup.string().notRequired().nullable(),
  preferencesDrink: yup.string().notRequired().nullable(),
  aversions: CheckboxWithOptionalTextSchema.notRequired().nullable().default({}),
  aids: yup.string().notRequired().nullable(),
  tube: yup
    .object({
      selected: yup.boolean().default(false),
      value: yup
        .mixed()
        .when('selected', {
          is: true,
          then: yup.object({
            type: yup.string().notRequired().nullable(),
            application: yup.string().notRequired().nullable(),
            notDaily: CheckboxSchema.notRequired().nullable().default({}),
            applicationType: yup
              .mixed()
              .oneOf([
                TubeApplicationType.INDEPENDENT,
                TubeApplicationType.UNINDEPENDENT,
                TubeApplicationType.COMPLETELY_UNINDEPENDENT,
                null,
              ])
              .notRequired()
              .nullable(),
            additionalFoodIntake: CheckboxWithOptionalTextSchema.notRequired()
              .nullable()
              .default({}),
            additionalLiquidIntake: CheckboxWithOptionalTextSchema.notRequired()
              .nullable()
              .default({}),
          }),
        })
        .notRequired()
        .nullable()
        .default({}),
    })
    .notRequired()
    .nullable()
    .default({}),
  goals: yup.string().notRequired().nullable(),
  dietType: yup.string().notRequired().nullable(),
  pureed: CheckboxWithOptionalTextSchema.notRequired().nullable().default({}),
  biteSized: CheckboxWithOptionalTextSchema.notRequired().nullable().default({}),
  foodIntakeIndependent: CheckboxSchema.notRequired().nullable().default({}),
  foodIntakePrepare: CheckboxSchema.notRequired().nullable().default({}),
  foodIntakeGuide: CheckboxSchema.notRequired().nullable().default({}),
  foodIntakeServe: CheckboxSchema.notRequired().nullable().default({}),
  foodIntakeChooseIndependent: CheckboxSchema.notRequired().nullable().default({}),
  foodProtocol: CheckboxSchema.notRequired().nullable().default({}),
  weightControlInterval: yup.number().notRequired().nullable(),
  weightControlNoMeasure: CheckboxSchema.notRequired().nullable().default({}),
  weightControlDecision: yup.string().notRequired().nullable(),
  drinkIntakeIndependent: CheckboxSchema.notRequired().nullable().default({}),
  drinkIntakePrepare: CheckboxSchema.notRequired().nullable().default({}),
  drinkIntakeGuide: CheckboxSchema.notRequired().nullable().default({}),
  drinkIntakeServe: CheckboxSchema.notRequired().nullable().default({}),
  drinkIntakeChooseIndependent: CheckboxSchema.notRequired().nullable().default({}),
  drinkProtocol: CheckboxSchema.notRequired().nullable().default({}),
  oralCare: CheckboxWithOptionalTextSchema.notRequired().nullable().default({}),
  aspirationProphylaxis: CheckboxWithOptionalTextSchema.notRequired().nullable().default({}),
  other: yup.string().notRequired().nullable(),
  activityFactor: yup.string().notRequired().nullable(),
  metabolicRate: yup.number().notRequired().nullable(),
  targetCalories: yup.number().notRequired().nullable(),
  targetDrinkIntake: yup.number().notRequired().nullable(),
  maxDrinkIntake: yup.number().notRequired().nullable(),
  mealPlans: yup.array(BaseMealSchema.notRequired().nullable().default({})).default([]),
});

export interface ICarePlan3 {
  prepareBiteSize: Partial<IScaleRating>;
  eat: Partial<IScaleRating>;
  drink: Partial<IScaleRating>;
  malnutritionRisk: ICheckbox;
  malnutrition: ICheckbox;
  exsiccosisRisk: ICheckbox;
  aspirationRisk: {
    selected?: boolean;
    value?: string[];
  };
  preferencesEat?: string;
  preferencesDrink?: string;
  aversions: ICheckboxWithText;
  aids: string;
  tube: {
    selected?: boolean;
    value?: ITube;
  };
  goals?: string;
  dietType?: string;
  pureed: ICheckboxWithText;
  biteSized: ICheckboxWithText;
  foodIntakeIndependent: ICheckbox;
  foodIntakePrepare: ICheckbox;
  foodIntakeGuide: ICheckbox;
  foodIntakeServe: ICheckbox;
  foodIntakeChooseIndependent: ICheckbox;
  foodProtocol: ICheckbox;
  weightControlInterval?: number;
  weightControlNoMeasure: ICheckbox;
  weightControlDecision?: WeightControlDecisionType | null;
  drinkIntakeIndependent: ICheckbox;
  drinkIntakePrepare: ICheckbox;
  drinkIntakeGuide: ICheckbox;
  drinkIntakeServe: ICheckbox;
  drinkIntakeChooseIndependent: ICheckbox;
  drinkProtocol: ICheckbox;
  oralCare: ICheckboxWithText;
  aspirationProphylaxis: ICheckboxWithText;
  other?: string;
  activityFactor?: string;
  metabolicRate?: number;
  targetCalories?: number;
  targetDrinkIntake?: number;
  maxDrinkIntake?: number;
  mealPlans: IMeal[];
}

export interface ITube {
  type?: string;
  application?: string;
  notDaily: ICheckbox;
  applicationType?: TubeApplicationType;
  additionalFoodIntake: ICheckboxWithText;
  additionalLiquidIntake: ICheckboxWithText;
}
